import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@core/services/environment.service';
import { delay, Observable, throwError } from 'rxjs';
import type { DxFlowRecord } from './dx-sinks';

/**
Ring buffer size for the Hanko flow timeline.
 */
const dxBufferSize = 50;

@Injectable({ providedIn: 'root' })
export class DxTelemetryService {
  private readonly flowRecordsSignal = signal<DxFlowRecord[]>([]);

  private readonly simulatedLatencyMsSignal = signal<number>(0);

  private readonly forcedHttpStatusSignal = signal<number>(0);

  public readonly flowRecords = this.flowRecordsSignal.asReadonly();

  public readonly simulatedLatencyMs = this.simulatedLatencyMsSignal.asReadonly();

  public readonly forcedHttpStatus = this.forcedHttpStatusSignal.asReadonly();

  /**
  Fault injection is an actuator and is honoured on local builds only.
   */
  public readonly isOverrideAllowed = inject(EnvironmentService).isLocal;

  /**
   * Appends a Hanko flow transition to the ring buffer. The Hanko SDK talks over
   * `XMLHttpRequest`, so its traffic never reaches the Angular HTTP interceptor -
   * this buffer is the only record of the auth path the suite has.
   *
   * @param {Omit<DxFlowRecord, 'id'>} record - Flow transition payload.
   */
  public logFlowTransition(record: Omit<DxFlowRecord, 'id'>): void {
    const newRecord: DxFlowRecord = { ...record, id: crypto.randomUUID() };
    this.flowRecordsSignal.update((list) => [newRecord, ...list].slice(0, dxBufferSize));
  }

  /**
   * Sets the simulated latency applied to intercepted requests.
   *
   * @param {number} latencyMs - Latency in milliseconds; 0 disables the override.
   */
  public setSimulatedLatency(latencyMs: number): void {
    this.simulatedLatencyMsSignal.set(latencyMs);
  }

  /**
   * Sets the HTTP status forced on intercepted requests.
   *
   * @param {number} status - Status to force; 0 disables the override.
   */
  public setForcedHttpStatus(status: number): void {
    this.forcedHttpStatusSignal.set(status);
  }

  /**
   * Clears captured Hanko flow transitions.
   */
  public clearFlowRecords(): void {
    this.flowRecordsSignal.set([]);
  }
}

/**
 * Produces a simulated HTTP failure, preserving the `HttpErrorResponse` shape so the
 * app's own error handling sees exactly what a real failure looks like.
 *
 * @param {HttpRequest<unknown>} request - The intercepted request.
 * @param {number} forcedStatus - HTTP status to simulate.
 * @param {number} latency - Simulated latency in milliseconds.
 * @returns {Observable<HttpEvent<unknown>>} A stream failing with the simulated response.
 */
function forceStatus(
  request: HttpRequest<unknown>,
  forcedStatus: number,
  latency: number,
): Observable<HttpEvent<unknown>> {
  const simulatedError = new HttpErrorResponse({
    status: forcedStatus,
    statusText: `DX Forced ${forcedStatus}`,
    url: request.url,
    error: {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      name: `DxForced${forcedStatus}Exception`,
      message: `DX interceptor simulated HTTP ${forcedStatus}`,
      statusCode: forcedStatus,
      code: -9999,
    },
  });

  const failure$ = throwError(() => simulatedError);
  return latency > 0 ? failure$.pipe(delay(latency)) : failure$;
}

/**
 * Functional HTTP interceptor applying the local-only edge-case overrides: a forced
 * status, an added latency, or neither.
 *
 * It records nothing. Request metadata - method, path, status, duration, correlation
 * ids - is what the browser's network tab already shows, and a buffer of it inside the
 * app is a copy of customer-session data that has to be gated, redacted and reviewed
 * for no diagnostic gain.
 *
 * @param {HttpRequest<unknown>} request - Outgoing HTTP request.
 * @param {HttpHandlerFn} next - Next handler function.
 * @returns {Observable<HttpEvent<unknown>>} Intercepted HTTP event stream.
 */
export function dxTelemetryInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const telemetryService = inject(DxTelemetryService);

  if (!telemetryService.isOverrideAllowed) {
    return next(request);
  }

  const latency = telemetryService.simulatedLatencyMs();
  const forcedStatus = telemetryService.forcedHttpStatus();

  if (forcedStatus > 0) {
    return forceStatus(request, forcedStatus, latency);
  }

  return latency > 0 ? next(request).pipe(delay(latency)) : next(request);
}
